<script>
import 'aframe'

import gamepads from 'linna/services/gamepads'

import { cameraRotationOffsetIntensity } from '@/config'

import animate from '@/util/animate'
import getOptionsString from '@/util/getOptionsString'

import SceneAssets from './SceneAssets'

export default {

  components: {
    SceneAssets
  },

  data () {
    return {
      assetsLoaded: false,

      renderedCameraRigPositionX: 0,
      renderedCameraRigPositionY: 0,
      renderedCameraRigPositionZ: 0,

      renderedCameraRotationDeg: 0
    }
  },

  computed: {

    lightEntities () {

      // Several lights
      return this.$store.getters.currentLevelLights.map(({ position, light }) => {
        const bindings = {}

        // Position as array
        if (position) {
          bindings.position = position.join(' ')
        }

        bindings.light = getOptionsString(light)

        return bindings
      })
    },



    // If no gamepad is connected, we fall back on placeholder values
    gamepadInputs () {
      return gamepads.firstConnectedGamepadInputs || gamepads.inputs[0]
    },

    // Basic camera options
    cameraOptions () {
      return getOptionsString({
        active: !this.$store.getters.playerCameraIsActive,
        fov: this.$store.getters.cameraFov,
        zoom: this.$store.getters.cameraZoom
      })
    },



    // Rig positioning: base position of imagined or real player on the field
    cameraRigPosition () {

      // Real player
      if (this.$store.getters.cameraFollowsPlayer) {
        return [
          this.$store.getters.renderedPlayerX,
          this.$store.getters.renderedPlayerY,
          0
        ]
      }

      // Imagined target
      return [
        this.$store.getters.cameraPositionX,
        this.$store.getters.cameraPositionY,
        0
      ]
    },

    renderedCameraRigPosition () {

      // Real player
      if (this.$store.getters.cameraFollowsPlayer) {
        return [
          this.$store.getters.renderedPlayerX,
          this.$store.getters.renderedPlayerY,
          this.renderedCameraRigPositionZ
        ]
      }

      return [
        this.renderedCameraRigPositionX,
        this.renderedCameraRigPositionY,
        this.renderedCameraRigPositionZ
      ]
    },

    renderedCameraRigRotation () {
      return [0, 0, this.renderedCameraRotationDeg]
    },



    // Camera within rig

    cameraPosition () {
      return [0, -this.$store.getters.cameraDistance, this.$store.getters.cameraElevation]
    },

    cameraRotation () {
      return [
        this.$store.getters.cameraRotationX - (2 * this.$store.getters.cameraElevation),
        this.$store.getters.cameraRotationY,
        this.$store.getters.cameraRotationZ
      ]
    },



    // Relative, fine-grained camera rotation
    // Small offset (meant for small adjustments with a controller stick)

    cameraRotationOffsetX () {
      return this.gamepadInputs.rightStickX
    },

    cameraRotationOffsetY () {
      return (this.$store.getters.invertCameraY ? -1 : 1) * this.gamepadInputs.rightStickY
    },

    cameraRotationOffsetZ () {
      return 0
    },

    cameraRotationOffset () {

      // Some additional adjustments to avoid optical illusions
      const xAxisRotation = cameraRotationOffsetIntensity * -this.cameraRotationOffsetY
      const additionalxAxisRotationMultiplier = (Math.pow((Math.abs(this.cameraRotationOffsetX) * 10), 2)) / 10
      const additionalxAxisRotation = -1 * 0.05 * (cameraRotationOffsetIntensity / 5) * additionalxAxisRotationMultiplier

      const yAxisRotation = cameraRotationOffsetIntensity * -this.cameraRotationOffsetX

      // Some additional adjustments to avoid optical illusions
      const zAxisRotation = (cameraRotationOffsetIntensity * this.cameraRotationOffsetZ)
      const additionalzAxisRotation = (cameraRotationOffsetIntensity / 2) * -this.cameraRotationOffsetX

      return [
        xAxisRotation + additionalxAxisRotation,
        yAxisRotation,
        zAxisRotation + additionalzAxisRotation
      ].join(' ')
    }

  },

  watch: {

    cameraRigPosition ([newX, newY, newZ]) {
      if (
        this.renderedCameraRigPositionX !== newX ||
        this.renderedCameraRigPositionY !== newY ||
        this.renderedCameraRigPositionZ !== newZ
      ) {
        this.animateCameraRigPositionTo([newX, newY, newZ])
      }
    },

    '$store.getters.cameraRotationStepDeg' (deg) {
      if (this.renderedCameraRotationDeg !== deg) {
        this.animateCameraRotationTo(deg)
      }
    }

  },

  created () {
    this.renderedCameraRigPositionX = this.cameraRigPosition[0]
    this.renderedCameraRigPositionY = this.cameraRigPosition[1]
    this.renderedCameraRigPositionZ = this.cameraRigPosition[2]

    this.renderedCameraRotationDeg = this.$store.getters.cameraRotationStepDeg
  },

  methods: {

    markAssetsLoaded () {
      this.assetsLoaded = true
    },

    markAssetsUnloaded () {
      this.assetsLoaded = false
    },

    async animateCameraRigPositionTo (targetPosition) {
      return animate(600, this.renderedCameraRigPosition, targetPosition, ([x, y, z]) => {

        if (this.renderedCameraRigPositionX !== x) {
          this.renderedCameraRigPositionX = x
        }

        if (this.renderedCameraRigPositionY !== y) {
          this.renderedCameraRigPositionY = y
        }

        if (this.renderedCameraRigPositionZ !== z) {
          this.renderedCameraRigPositionZ = z
        }

      })
    },

    async animateCameraRotationTo (targetRotationDeg) {
      return animate(600, this.renderedCameraRotationDeg, targetRotationDeg, (frameRotationDeg) => {
        this.renderedCameraRotationDeg = frameRotationDeg
      })
    }

  }

}
</script>

<template>
  <a-scene
    embedded
    class="c-level-scene"
  >

    <!-- Lights -->
    <a-entity
      v-for="(bindings, i) in lightEntities"
      :key="i"
      v-bind="bindings"
    />



    <!-- A-Frame needs us to declare certain assets directly under the a-scene component -->
    <scene-assets @loaded="markAssetsLoaded" />



    <!-- Centered part of rig that will rotate around environment -->
    <a-entity
      ref="cameraRig"
      :data-position-value="renderedCameraRigPosition.join(' ')"
      :position="renderedCameraRigPosition.join(' ')"
      :rotation="renderedCameraRigRotation.join(' ')"
    >

      <!-- This part will rotate and zoom -->
      <a-entity
        ref="cameraRotater"
        :rotation="cameraRotation.join(' ')"
        :position="cameraPosition.join(' ')"
      >

        <!-- This part will rotate and zoom -->
        <a-entity
          ref="controllableCamera"
          :rotation="cameraRotationOffset"
        >

          <!-- Camera with out look controls -->
          <a-entity
            ref="camera"
            :camera="cameraOptions"
          />
        </a-entity>
      </a-entity>
    </a-entity>

    <!-- Content goes here -->
    <slot v-if="assetsLoaded" />
  </a-scene>
</template>

<style lang="scss">

.c-level-scene {
  position: relative;

  > .a-enter-vr {
    @include hide;
  }

}

</style>
